import type { RnbCalculatorPayload } from 'typings'
import { toAPIOriginalValues, toAPIPromotionBase } from '..'
import {
  ProgressiveDiscountConditions,
  ProgressiveDiscountEffect,
  ProgressiveDiscountPromotion,
} from '../../../../typings/progressiveDiscount'
import {
  toAPIBin,
  toAPICluster,
  toAPIPaymentMethods,
  toAPIShippingLocation,
  toAPIUtm,
} from '../conditions'
import { toAPIRestrictions } from '../restrictions'

const PROGRESSIVE_DISCOUNT_INCOMPATIBLE_FIELDS: (keyof RnbCalculatorPayload)[] =
  [
    // Discount section
    'absoluteShippingDiscountValue',
    'nominalDiscountType',
    'nominalShippingDiscountValue',
    'nominalDiscountValue',
    'maximumUnitPriceDiscount',
    'percentualDiscountValue',
    'rebatePercentualDiscountValue',
    'percentualShippingDiscountValue',
    'percentualTax',
    'shippingPercentualTax',
    'taxExpression',
    'discountExpression',
    'percentualDiscountValueList1',
    'percentualDiscountValueList2',
    'activateGiftsMultiplier',
    'nominalRewardValue',
    'percentualRewardValue',
    'orderStatusRewardValue',
    'nominalTax',
    'priceTableName',
    'percentualDiscountValueList',

    // Buy and win section
    'skusGift',
    'collections1BuyTogether',
    'collections2BuyTogether',
    'minimumQuantityBuyTogether',
    'quantityToAffectBuyTogether',
    'enableBuyTogetherPerSku',
    'listSku1BuyTogether',
    'listSku2BuyTogether',
  ]

type APIProgressiveDiscountConditions = ReturnType<
  typeof toAPIPaymentMethods &
    typeof toAPIBin &
    typeof toAPIShippingLocation &
    typeof toAPIUtm &
    typeof toAPICluster
>

type APIProgressiveDiscountSectionPayload = Pick<
  Partial<RnbCalculatorPayload>,
  | 'listSku1BuyTogether'
  | 'collections1BuyTogether'
  | 'enableBuyTogetherPerSku'
  | 'percentualDiscountValueList'
>

export function toAPIProgressiveDiscountEffect(
  config: ProgressiveDiscountEffect
): APIProgressiveDiscountSectionPayload {
  const { trigger, benefit } = config

  if (!trigger || !benefit)
    throw new Error(
      'Cannot convert Promotion to API Payload: Invalid Progressive Discount Effect',
      { cause: { payload: config } }
    )

  return {
    listSku1BuyTogether: trigger.skus,
    collections1BuyTogether: trigger.collections,
    enableBuyTogetherPerSku: trigger.sameItemsOnly,
    percentualDiscountValueList: benefit.map(({ discount, quantity }) => ({
      percent: discount,
      quantity,
    })),
  }
}

export function toAPIProgressiveDiscountConditions(
  conditions: ProgressiveDiscountConditions
): APIProgressiveDiscountConditions {
  return {
    ...toAPIPaymentMethods(conditions.paymentMethods),
    ...toAPIBin(conditions.bin),
    ...toAPIUtm(conditions.utm),
    ...toAPICluster(conditions.cluster),
    ...toAPIShippingLocation(conditions.shippingLocation),
  }
}

type APIProgressiveDiscountPayload = ReturnType<
  typeof toAPIOriginalValues &
    typeof toAPIProgressiveDiscountEffect &
    typeof toAPIProgressiveDiscountConditions &
    typeof toAPIRestrictions &
    typeof toAPIPromotionBase
>

export function toAPIProgressiveDiscount(
  data: ProgressiveDiscountPromotion,
  originalValues?: RnbCalculatorPayload
): APIProgressiveDiscountPayload {
  return {
    ...toAPIOriginalValues(
      originalValues,
      PROGRESSIVE_DISCOUNT_INCOMPATIBLE_FIELDS
    ),
    ...toAPIPromotionBase(data),
    ...toAPIProgressiveDiscountEffect(data.progressive),
    ...toAPIProgressiveDiscountConditions(data.conditions),
    ...toAPIRestrictions(data.restrictions),
  }
}
